/**
 * The page a customer's change-order link opens.
 *
 * `/c/<token>`, the sibling of `/p/<token>`: no session, no row level security
 * to satisfy, no data of ours in the bundle. Everything comes from the
 * `change-order` Edge Function, which is the only thing holding a key, and if
 * that function is unreachable this page shows a phone number rather than a
 * stack trace.
 *
 * The same page is what "Sign now" opens when the rep hands their phone over at
 * the kitchen table, so there is one signing surface and no second code path to
 * keep honest.
 *
 * Deliberately short. A customer meeting this has already read and signed a
 * contract; what they need now is what changed, what it costs, what the job
 * comes to, and a signature — in that order, on one screen, with nothing to
 * scroll past.
 */

import {
  changeOrderAfterWithdrawals,
  changeOrderSignatureIsComplete,
  changeOrderSigningSteps,
  formatMoney,
  nextChangeOrderStep,
  signedDateWords,
  type ChangeOrderDocument,
  type PaymentMethod,
  type ChangeOrderWithdrawal,
} from "@roof-hawk/core";
import { useCallback, useEffect, useState } from "react";

import { PaymentChoicePage, type PayBlock } from "./PaymentChoicePage";

/** `/c/<32 hex characters>` and nothing else. */
export function changeOrderTokenFromPath(pathname: string): string | null {
  const match = /^\/c\/([0-9a-f]{32})\/?$/.exec(pathname);
  return match ? match[1]! : null;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/change-order`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface ChangeOrderSignature {
  signerName: string;
  signerEmail: string | null;
  signedDate: string | null;
  acceptedAt: string;
}

type Loaded =
  | { state: "loading" }
  | { state: "open"; document: ChangeOrderDocument }
  | {
      state: "signed";
      document: ChangeOrderDocument;
      signature: ChangeOrderSignature | null;
      /**
       * What there is left to pay on the job, and how. Null when payments are
       * switched off, when Roof Board has never asked this job for money, or
       * when they are paid up — and then this page is what it always was.
       */
      pay: PayBlock | null;
      withdrawn: ChangeOrderWithdrawal[];
    }
  | { state: "closed"; message: string }
  | { state: "error" };

/** Today, as the customer's own device reckons it. Never through `new Date`. */
function todayLocal(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function ChangeOrderPage({ token }: { token: string }) {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Every call is a POST, reading included — the token is the only credential
   * in this flow, and a query string is written to request logs, kept in
   * browser history and sent along in a Referer.
   */
  const call = useCallback(
    async (action: "open" | "sign" | "pay", payload?: object) => {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, action, ...payload }),
      });
      return { response, body: await response.json() };
    },
    [token],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { response, body } = await call("open");
        if (cancelled) return;
        if (response.ok && body.state === "open") {
          setLoaded({ state: "open", document: body.document });
        } else if (response.ok && body.state === "signed") {
          // An older function cannot say whether a signed line was withdrawn.
          // Do not turn a missing field into "none" and show an old total.
          if (!Array.isArray(body.withdrawn)) {
            setLoaded({ state: "error" });
            return;
          }
          setLoaded({
            state: "signed",
            document: body.document,
            signature: body.signature ?? null,
            pay: (body.pay as PayBlock | null) ?? null,
            withdrawn: body.withdrawn,
          });
        } else if (body?.message) {
          setLoaded({ state: "closed", message: body.message });
        } else {
          setLoaded({ state: "error" });
        }
      } catch {
        if (!cancelled) setLoaded({ state: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [call]);

  const sign = async (payload: {
    consented: boolean;
    signerName: string;
    signerEmail: string;
    signedDate: string;
  }) => {
    setSigning(true);
    setError(null);
    try {
      const { response, body } = await call("sign", payload);
      if (response.ok && body.state === "signed") {
        if (!Array.isArray(body.withdrawn)) {
          setLoaded({ state: "error" });
          return;
        }
        setLoaded({
          state: "signed",
          document: body.document,
          signature: body.signature ?? null,
          pay: (body.pay as PayBlock | null) ?? null,
          withdrawn: body.withdrawn,
        });
      } else {
        setError(
          body?.error ??
            body?.message ??
            "That did not go through. Please try again, or call us.",
        );
      }
    } catch {
      setError("That did not go through. Please try again, or call us.");
    } finally {
      setSigning(false);
    }
  };

  if (loaded.state === "loading") return <div className="pdoc-wait" />;

  if (loaded.state === "error") {
    return (
      <Message
        title="We cannot open this right now"
        body="Please call us and we will sort it out."
      />
    );
  }

  if (loaded.state === "closed") {
    return <Message title="This change order" body={loaded.message} />;
  }

  /**
   * Signed, with a balance to settle: the payment screen, and their copy under
   * it.
   *
   * The same shape the signed contract lands on (PresentPage), because it is
   * the same moment — Jonathon, 2026-09-22: approving a change order "needs to
   * take them to the screen where they can make their payment". A change order
   * with nothing to pay falls through to the page it always was, which is what
   * every job looks like while Mercury is switched off.
   */
  if (
    loaded.state === "signed" &&
    loaded.pay &&
    (loaded.pay.dueCents > 0 || loaded.pay.chosen || loaded.pay.paid)
  ) {
    const block = loaded.pay;
    return (
      <>
        <PaymentChoicePage
          pay={block}
          onChoose={async (method: PaymentMethod) => {
            try {
              const { response, body } = await call("pay", { method });
              if (!response.ok) {
                return { error: body?.error as string | undefined };
              }
              // The block comes back with the choice on it, so a redirect that
              // never happens still leaves the page telling the truth.
              if (body?.pay) {
                setLoaded({ ...loaded, pay: body.pay as PayBlock });
              }
              return { paymentUrl: body?.paymentUrl as string | null };
            } catch {
              return {
                error:
                  "We could not reach our payment page. Please try again, or " +
                  "call us.",
              };
            }
          }}
        />
        <SignedCopyPanel
          document={loaded.document}
          signature={loaded.signature}
          withdrawn={loaded.withdrawn}
        />
      </>
    );
  }

  return (
    <ChangeOrderView
      document={loaded.document}
      signature={loaded.state === "signed" ? loaded.signature : null}
      withdrawn={loaded.state === "signed" ? loaded.withdrawn : []}
      onSign={sign}
      signing={signing}
      error={error}
    />
  );
}

/**
 * "Your change order", under the payment cards.
 *
 * Closed by default and open in one tap, like the signed agreement's: somebody
 * who has just approved something is being asked to pay and does not need the
 * document unrolled underneath that, while somebody who came back a week later
 * wants to read it. Nothing is re-rendered — it is the same frozen page, drawn
 * by the same component the office and the rep see (ADR 0032).
 */
function SignedCopyPanel({
  document,
  signature,
  withdrawn,
}: {
  document: ChangeOrderDocument;
  signature: ChangeOrderSignature | null;
  withdrawn: ChangeOrderWithdrawal[];
}) {
  const [open, setOpen] = useState(false);
  const signedOn = signedDateWords(signature?.signedDate ?? null);
  return (
    <div className="pdoc-copy">
      <div className="pdoc-copy-head">
        <div>
          <h2>Your change order</h2>
          <p>
            {signedOn
              ? `Approved on ${signedOn}. This is your copy — read it, print it, or save it.`
              : "This is your copy — read it, print it, or save it."}
          </p>
        </div>
        <button
          type="button"
          className="pdoc-toggle"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          {open ? "Hide it" : "Read it"}
        </button>
      </div>
      {open ? (
        <ChangeOrderView
          document={document}
          signature={signature}
          withdrawn={withdrawn}
          onSign={() => undefined}
        />
      ) : null}
    </div>
  );
}

/**
 * The document itself, signed or not.
 *
 * One component for both, like the agreement: a signed change order shows the
 * same page with the signature where the form was, so a customer coming back
 * to it and the office reading it in Docs are looking at the same thing.
 */
export function ChangeOrderView({
  document,
  signature,
  withdrawn,
  onSign,
  readOnly = false,
  signing,
  error,
}: {
  document: ChangeOrderDocument;
  signature: ChangeOrderSignature | null;
  /**
   * Signed lines deleted since (ADR 0181). Shown beside what was signed,
   * never in place of it: the snapshot is the record.
   */
  withdrawn?: readonly ChangeOrderWithdrawal[];
  onSign: (payload: {
    consented: boolean;
    signerName: string;
    signerEmail: string;
    signedDate: string;
  }) => void | Promise<void>;
  /** Office records can be opened before approval, but cannot be signed here. */
  readOnly?: boolean;
  signing?: boolean;
  error?: string | null;
}) {
  const [termsOpen, setTermsOpen] = useState(false);
  const [consented, setConsented] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signedDate] = useState(todayLocal);

  const stepInput = { termsOpen, consented, signerName };
  const steps = signature || readOnly ? [] : changeOrderSigningSteps(stepInput);
  const now = signature || readOnly ? null : nextChangeOrderStep(stepInput).id;
  const focus = (step: string) => (now === step ? " pdoc-focus" : "");
  const ready = changeOrderSignatureIsComplete({ consented, signerName });
  // Only a signed document can have a withdrawn line: the database records
  // one only when a signed line is deleted.
  const after = signature
    ? changeOrderAfterWithdrawals(document, withdrawn)
    : null;

  return (
    <div className="pdoc cdoc">
      <header className="cdoc-head">
        <div className="wrap">
          <p className="cdoc-co">{document.company.name}</p>
          <h1>Change order {document.number}</h1>
          <p className="cdoc-sub">
            {[
              document.job.number ? `Job ${document.job.number}` : null,
              document.job.address,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </header>

      <section className="cdoc-sec">
        <div className="wrap">
          <p className="cdoc-lead">
            {signature
              ? "This is your copy of the change order you approved."
              : "Something came up on your job that was not in the agreement " +
                "you signed. Here is what it is and what it costs."}
          </p>

          {document.lines.map((line) => (
            <div
              key={line.id}
              className={`cdoc-line${
                after?.withdrawnLineIds.has(line.id) ? " withdrawn" : ""
              }`}
            >
              <div className="cdoc-line-head">
                <strong>{line.description}</strong>
                <span>{formatMoney(line.amount)}</span>
              </div>
              {after?.withdrawnLineIds.has(line.id) ? (
                <p className="cdoc-withdrawn">Withdrawn</p>
              ) : null}
              {line.scope ? <p className="cdoc-scope">{line.scope}</p> : null}
              <p className="cdoc-qty">
                {line.qty}
                {line.unit ? ` ${line.unit}` : ""} ×{" "}
                {formatMoney(line.unitPrice)}
              </p>
            </div>
          ))}

          <div className="cdoc-tot">
            <div>
              <span>Your agreement before this</span>
              <span>{formatMoney(document.contractBefore)}</span>
            </div>
            <div>
              <span>This change</span>
              <span>+ {formatMoney(document.changeTotal)}</span>
            </div>
            {after ? (
              <div>
                <span>Withdrawn</span>
                <span>− {formatMoney(after.withdrawnTotal)}</span>
              </div>
            ) : null}
            <div className="cdoc-tot-sum">
              <span>Your job now comes to</span>
              <span>
                {formatMoney(
                  after ? after.contractAfterNow : document.contractAfter,
                )}
              </span>
            </div>
            {/*
              What they have already paid, and what that leaves. Jonathon,
              2026-09-22: the change order has to reflect the balance they have
              already paid. Frozen with the rest of the page when the rep
              pressed Present, and left off entirely on a job nobody has paid
              anything on — and on every document presented before this
              existed, which carries neither figure.
            */}
            {document.paidToDate != null && document.paidToDate > 0 ? (
              <>
                <div>
                  <span>You have paid so far</span>
                  <span>− {formatMoney(document.paidToDate)}</span>
                </div>
                <div className="cdoc-tot-sum">
                  <span>Balance on your job</span>
                  <span>
                    {formatMoney(document.balanceAfter ?? (0 as never))}
                  </span>
                </div>
              </>
            ) : null}
            <p className="cdoc-fine">
              Your deposit does not change. You can pay your balance as soon as
              you have approved this, or leave it until the work is finished.
            </p>
          </div>
        </div>
      </section>

      <section className="cdoc-sec cdoc-signing">
        <div className="wrap">
          {steps.length > 0 ? (
            <ol className="pdoc-steps" aria-label="What to do to approve this">
              {steps.map((step) => (
                <li
                  key={step.id}
                  className={`${step.done ? "done" : ""}${
                    step.id === now ? " now" : ""
                  }`}
                  aria-current={step.id === now ? "step" : undefined}
                >
                  {step.label}
                </li>
              ))}
            </ol>
          ) : null}

          <button
            type="button"
            className={`pdoc-toggle${focus("read")}`}
            aria-expanded={termsOpen}
            onClick={() => setTermsOpen((was) => !was)}
          >
            {termsOpen ? "Hide the terms" : "Read the terms"}
          </button>

          {termsOpen ? (
            <div className="pdoc-legal cdoc-legal">
              {document.terms.map((term) => (
                <p key={term.id}>{term.body}</p>
              ))}
            </div>
          ) : null}

          {signature ? (
            <div className="cdoc-signed">
              <p className="cdoc-signed-by">
                Approved by <strong>{signature.signerName}</strong>
                {signedDateWords(signature.signedDate)
                  ? ` on ${signedDateWords(signature.signedDate)}`
                  : ""}
                .
              </p>
              <button
                type="button"
                className="pdoc-toggle"
                onClick={() => window.print()}
              >
                Print or save
              </button>
            </div>
          ) : readOnly ? (
            <p className="cdoc-signed-by">Awaiting customer approval.</p>
          ) : (
            <div className="pdoc-sign">
              <label className={`pdoc-consent${focus("consent")}`}>
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(e) => setConsented(e.target.checked)}
                />
                <span>
                  I agree to do business electronically and to sign this change
                  order with an electronic signature.
                </span>
              </label>

              <label className={focus("name").trim()}>
                Your full name
                <input
                  type="text"
                  value={signerName}
                  autoComplete="name"
                  onChange={(e) => setSignerName(e.target.value)}
                />
              </label>

              <label>
                Your email, so we can send you a copy
                <input
                  type="email"
                  value={signerEmail}
                  autoComplete="email"
                  onChange={(e) => setSignerEmail(e.target.value)}
                />
              </label>

              {error ? <p className="cdoc-error">{error}</p> : null}

              <button
                type="button"
                className="cdoc-go"
                disabled={!ready || signing}
                onClick={() =>
                  void onSign({
                    consented,
                    signerName: signerName.trim(),
                    signerEmail: signerEmail.trim(),
                    signedDate,
                  })
                }
              >
                {signing ? "One moment…" : "Approve this change order"}
              </button>
              <p className="cdoc-fine">
                We will email you a copy, and our office keeps one.
              </p>
            </div>
          )}

          {document.rep ? (
            <p className="cdoc-rep">
              Questions? Call {document.rep.name}
              {document.rep.phone ? ` on ${document.rep.phone}` : ""}.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <div className="pdoc pdoc-message">
      <h1>{title}</h1>
      <p>{body}</p>
    </div>
  );
}
